/**
 * Native IMAP client — transport-agnostic.
 * Uses ImapTransport for I/O, imap-protocol for parsing.
 * Works with NodeTransport (desktop) or BridgeTransport (Android).
 *
 * This is a NEW client alongside the existing ImapClient (which wraps imapflow).
 * Existing callers are not affected.
 */

import type { ImapTransport, TransportFactory } from "./transport.js";
import * as proto from "./imap-protocol.js";
import type { ImapClientConfig, TokenProvider } from "./types.js";

// Yield the event loop between buffer slices. Node has setImmediate (drains
// I/O + timers + IPC before resuming); browser / Android (BridgeTransport)
// runtimes don't, so fall back to a 0 ms timer. Reached via globalThis so this
// transport-agnostic package needs no node type dependency.
const yieldToEventLoop: (cb: () => void) => void =
    typeof (globalThis as any).setImmediate === "function"
        ? (globalThis as any).setImmediate
        : (cb: () => void) => { setTimeout(cb, 0); };

export interface NativeFetchedMessage {
    seq: number;
    uid: number;
    flags: Set<string>;
    date: Date | null;            // INTERNALDATE — server-received time
    sentDate?: Date;              // ENVELOPE Date: header — original send time
    subject: string;
    messageId: string;
    from: proto.AddressData[];
    to: proto.AddressData[];
    cc: proto.AddressData[];
    bcc: proto.AddressData[];
    sender: proto.AddressData[];
    replyTo: proto.AddressData[];
    inReplyTo: string;
    size: number;
    source: string;
    headers: string;
    // Convenience flags
    seen: boolean;
    flagged: boolean;
    answered: boolean;
    draft: boolean;
    /** Per-message modification sequence (RFC 7162). Present when the
     *  server is CONDSTORE/QRESYNC-capable and the client either has
     *  ENABLE QRESYNC active or asked for MODSEQ explicitly. Used by the
     *  caller to track its own `last_modseq` watermark per folder. */
    modSeq?: number;
}

export interface NativeFolder {
    path: string;
    delimiter: string;
    flags: string[];
}

export interface MailboxInfo {
    exists: number;
    recent: number;
    uidNext: number;
    uidValidity: number;
    flags: string[];
    permanentFlags: string[];
    /** Per RFC 7162. Present when the server is CONDSTORE/QRESYNC-capable
     *  and includes [HIGHESTMODSEQ N] in the SELECT-OK response (or after
     *  ENABLE QRESYNC). Use as the `since-modseq` argument to subsequent
     *  resyncs to ask the server "what changed since this point?" */
    highestModSeq?: number;
}

/** Parameters for `SELECT mailbox (QRESYNC (uidvalidity modseq))` per RFC 7162.
 *  Caller persists `(uidValidity, modSeq)` per folder; after ENABLE QRESYNC
 *  has been issued once on the session, the next SELECT replies with
 *  `* VANISHED (EARLIER) <uids>` for every message the server expunged since
 *  `modSeq`, plus `* FETCH` for every message whose state changed since then.
 *  No client-side UID diffing, no tombstones — the server tells you the
 *  authoritative deletions. */
export interface QresyncParams {
    uidValidity: number;
    modSeq: number;
    knownUids?: string;  // optional UID range hint, e.g. "1:100"
}

/** Result of a QRESYNC-enabled SELECT. The caller drains `vanishedUids`
 *  (delete from local store), processes `changedMessages` (upsert state
 *  changes), and saves the new `highestModSeq` for the next resync. */
export interface SelectResult extends MailboxInfo {
    vanishedUids: number[];
    /** Untagged `* FETCH` responses the server emitted during the QRESYNC
     *  SELECT — typically flag/MODSEQ changes for messages whose state
     *  shifted between the caller's previous modSeq and now. Empty for
     *  non-QRESYNC SELECTs. */
    changedMessages: NativeFetchedMessage[];
    /** True if the server's UIDVALIDITY changed since the caller's last
     *  visit — in that case knownUids/modSeq are invalid and the caller
     *  must full-resync from scratch. */
    uidValidityChanged: boolean;
}

export class NativeImapClient {
    private transport: ImapTransport;
    private transportFactory: TransportFactory;
    private config: ImapClientConfig;
    /** Byte-level receive buffer. IMAP literals (`{N}`) are octets — message
     *  bodies, MIME parts with binary content, non-UTF-8 charsets — and the
     *  parser MUST work in bytes to recover them with byte fidelity and
     *  without paying O(n²) re-encoding cost on every chunk arrival. The
     *  prior implementation kept `this.buffer` as a JS string, accumulated
     *  UTF-8-decoded text from the transport, then `TextEncoder.encode`'d
     *  the whole unread region on every TCP chunk while waiting for a
     *  literal — pegged the event loop on multi-message FETCH responses.
     *
     *  Invariants:
     *    - `buffer[0 .. bufferLength)` holds valid received bytes
     *    - `buffer[bufferOffset .. bufferLength)` is unread
     *    - capacity = `buffer.length` ≥ `bufferLength`; we grow on demand
     *    - never substring — bufferOffset advances past consumed bytes;
     *      compactBufferIfNeeded reclaims space when consumed >= half. */
    private buffer: Uint8Array = new Uint8Array(8192);
    private bufferLength = 0;
    private bufferOffset = 0;
    /** Cached decoder so per-line / per-literal decode doesn't re-construct
     *  one. `fatal: false` (default) replaces invalid UTF-8 sequences with
     *  U+FFFD — same lenient behavior as JavaScript's implicit string
     *  decoding the parser used to rely on. */
    private utf8Decoder = new TextDecoder("utf-8");
    private pendingCommand: {
        tag: string;
        resolve: (responses: proto.ImapResponse[]) => void;
        reject: (err: Error) => void;
        responses: proto.ImapResponse[];
        literalBytes?: number;
        literalBuffer?: string;
        /** Track literal key (e.g. "BODY[]") and size for the current literal being read */
        currentLiteralKey?: string;
        currentLiteralSize?: number;
        /** Accumulated literals keyed by section name */
        literals?: Map<string, string>;
        /** Called for each untagged response as it arrives (e.g. per-message FETCH streaming) */
        onUntagged?: (resp: proto.ImapResponse) => void;
    } | null = null;
    private capabilities = new Set<string>();
    private _connected = false;
    private idleTag: string | null = null;
    private idleCallback: ((count: number) => void) | null = null;
    /** Fires when the SELECTED mailbox loses messages while parked in IDLE —
     *  an EXISTS decrease, or an unsolicited `* EXPUNGE` / `* VANISHED`
     *  (RFC 5465 NOTIFY SELECTED MessageExpunge). Lets the caller reconcile
     *  a server-side deletion in real time instead of waiting for the next
     *  periodic poll. Distinct from `idleCallback` (new mail only). */
    private idleExpungeCallback: (() => void) | null = null;
    /** Fired for an unsolicited FETCH carrying FLAGS while parked in IDLE —
     *  another client (a phone, Thunderbird, webmail) changed a flag on a
     *  message in the selected mailbox. */
    private idleFlagCallback: ((seq: number, flags: string[]) => void) | null = null;
    private idleRefreshTimer: ReturnType<typeof setTimeout> | null = null;
    /** RFC 5465 NOTIFY: fires on unsolicited STATUS responses for non-selected
     *  mailboxes (the server pushes these when the client has issued NOTIFY
     *  SET with a PERSONAL group). Distinct from `idleCallback` which only
     *  fires for EXISTS on the currently-selected mailbox. */
    public onMailboxStatus: ((mailbox: string, data: proto.StatusData) => void) | null = null;
    /** Set by startIdle's stop closure so auto-suspend knows not to resume after a command finishes. */
    private idleStopped = true;
    private verbose: boolean;
    private selectedMailbox: string | null = null;
    private mailboxInfo: MailboxInfo = { exists: 0, recent: 0, uidNext: 0, uidValidity: 0, flags: [], permanentFlags: [] };
    private greetingResolve: ((resp: proto.ImapResponse) => void) | null = null;
    /** Callback for waitForContinuation — set when waiting for "+" response */
    private continuationResolve: ((resp: proto.ImapResponse) => void) | null = null;
    /** Per-command heartbeat (30s). Hoisted to instance state so any path
     *  that swaps pendingCommand can clear it cleanly — closure-scoped
     *  timers leaked when handleData's inline replacement or IDLE
     *  suspend/resume bypassed sendCommandCore's resolve/reject closures. */
    private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    /** Hard wall-clock cap per command. Same hoist for the same reason. */
    private wallClockTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(config: ImapClientConfig, transportFactory: TransportFactory) {
        this.config = config;
        this.transportFactory = transportFactory;
        this.transport = transportFactory();
        this.verbose = config.verbose || false;
        this.inactivityTimeout = config.inactivityTimeout ?? 60000;
        // Hard wall-clock deadline per command. Distinct from inactivityTimeout
        // — that one resets on each data chunk and can be deferred indefinitely
        // by a slow-trickling server. Default 90s: in production we see
        // connections silently die with sinceLastRead climbing for 50s+ on
        // FETCH; 5 min was leaving them wedged for too long, blocking sync /
        // prefetch / new-mail behind a dead socket. 90s lets a slow but
        // genuine FETCH complete (Sent in isolation: 26ms; even a 50-message
        // FETCH was <1s) while killing dead sockets aggressively. Override
        // via ImapClientConfig.commandWallClockTimeout for known-slow servers.
        this.commandWallClockTimeout = config.commandWallClockTimeout ?? 90_000;
        this.fetchChunkSize = config.fetchChunkSize ?? 25;
        this.fetchChunkSizeMax = config.fetchChunkSizeMax ?? 500;
        this.greetingTimeout = config.greetingTimeout ?? 10000;
    }

    get connected(): boolean { return this._connected; }

    /** Check the underlying transport's connected state — catches silently dead sockets. */
    get transportConnected(): boolean { return this.transport?.connected ?? false; }

    /** Replace the transport with a fresh one from the factory. Call before reconnect
     *  when the old transport is in a bad state (dead socket, stale bridge stream). */
    resetTransport(): void {
        try { this.transport?.close?.(); } catch { /* ignore */ }
        this.transport = this.transportFactory();
        this._connected = false;
        this.bufferLength = 0;
        this.bufferOffset = 0;
        this.pendingCommand = null;
        this.selectedMailbox = null;
        // Old socket is gone — kill any timers still running for the
        // command that was on it. Without this, a stale heartbeat/wall-clock
        // continues firing into a transport that no longer exists.
        this.clearAllCommandTimers();
    }

    // ── Connection ──

    async connect(): Promise<void> {
        const useTls = this.config.port === 993;

        this.transport.onData((data) => this.handleData(data));
        this.transport.onClose(() => {
            this._connected = false;
            // Drop stale IDLE state — the socket is gone.
            this.idleTag = null;
            this.idleCallback = null;
            this.idleStopped = true;
            if (this.idleRefreshTimer) { clearTimeout(this.idleRefreshTimer); this.idleRefreshTimer = null; }
            // Kill any per-command timers — the connection is gone, no
            // command on it can ever complete. Otherwise heartbeat keeps
            // firing for a connection that no longer exists.
            this.clearAllCommandTimers();
            if (this.pendingCommand) {
                const { reject } = this.pendingCommand;
                this.pendingCommand = null;
                reject(new Error("Connection closed"));
            }
        });
        this.transport.onError((err) => {
            // Diagnose transport-level errors fully. Node emits socket errors
            // with a populated `.message` in normal cases (ECONNRESET, ETIMEDOUT,
            // etc.) — but TLS-layer errors, abrupt FINs surfaced as errors,
            // and some wrapper paths produce an Error with empty `.message`
            // and the useful info on `.code` / `.errno` / `.syscall` instead.
            // Logging only `.message` left "Transport error: " in the log
            // with no diagnostic content, which masked why bobma INBOX prefetch
            // kept dying (2026-05-27). Build a richer description.
            const errAny = err as any;
            const parts: string[] = [];
            if (errAny?.message) parts.push(String(errAny.message));
            if (errAny?.code) parts.push(`code=${errAny.code}`);
            if (errAny?.errno !== undefined) parts.push(`errno=${errAny.errno}`);
            if (errAny?.syscall) parts.push(`syscall=${errAny.syscall}`);
            const desc = parts.length > 0
                ? parts.join(" ")
                : `<no message> ${typeof err} ${err?.constructor?.name || ""}`.trim();
            if (this.verbose) console.error(`  [imap] Transport error: ${desc}`);
            // Transport errors (DNS failure, ECONNRESET, TLS failure, etc.) mean
            // the connection is dead — clear the flag so ensureConnected() will
            // reconnect on the next call, and drop the stale IDLE state so we
            // don't try to DONE a dead socket during auto-suspend.
            this._connected = false;
            this.idleTag = null;
            this.idleCallback = null;
            this.idleStopped = true;
            if (this.idleRefreshTimer) { clearTimeout(this.idleRefreshTimer); this.idleRefreshTimer = null; }
            if (this.pendingCommand) {
                const { reject } = this.pendingCommand;
                this.pendingCommand = null;
                // Make sure the error that propagates to upper-layer catches
                // (prefetch, sync, etc.) has a non-empty message — wrap if
                // the underlying err has a blank message.
                if (err instanceof Error && !err.message) {
                    const wrapped = new Error(desc);
                    (wrapped as any).cause = err;
                    reject(wrapped);
                } else {
                    reject(err);
                }
            }
        });

        await this.transport.connect(this.config.server, this.config.port, useTls, this.config.server);

        // Read server greeting
        const greeting = await this.readGreeting();
        if (this.verbose) console.log(`  [imap] Greeting: ${greeting.raw}`);

        // Parse capabilities from greeting
        if (greeting.text.includes("CAPABILITY")) {
            this.parseCapabilities(greeting.text);
        } else {
            await this.capability();
        }

        // STARTTLS if needed
        if (!useTls && this.capabilities.has("STARTTLS")) {
            await this.starttls();
        }

        this._connected = true;

        // Authenticate
        await this.authenticate();
    }

    private async readGreeting(): Promise<proto.ImapResponse> {
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                this.greetingResolve = null;
                reject(new Error(`Greeting timeout (${this.greetingTimeout / 1000}s)`));
            }, this.greetingTimeout);
            this.greetingResolve = (resp) => {
                clearTimeout(timeout);
                resolve(resp);
            };
        });
    }

    private async authenticate(): Promise<void> {
        if (this.config.tokenProvider) {
            const token = await this.config.tokenProvider();
            const tag = proto.nextTag();
            const cmd = proto.xoauth2Command(tag, this.config.username, token);
            if (this.verbose) console.log(`  [imap] > AUTHENTICATE XOAUTH2 ...`);
            const responses = await this.sendCommand(tag, cmd);
            const tagged = responses.find(r => r.tag === tag);
            if (!tagged || tagged.type !== "OK") {
                const errText = tagged?.text || responses.map(r => r.raw).join("; ");
                throw new Error(`Authentication failed: ${errText}`);
            }
        } else if (this.config.password) {
            const tag = proto.nextTag();
            const cmd = proto.loginCommand(tag, this.config.username, this.config.password);
            if (this.verbose) console.log(`  [imap] > LOGIN ${this.config.username} ***`);
            const responses = await this.sendCommand(tag, cmd);
            const tagged = responses.find(r => r.tag === tag);
            if (!tagged || tagged.type !== "OK") {
                const errText = tagged?.text || responses.map(r => r.raw).join("; ");
                throw new Error(`Login failed: ${errText}`);
            }
        } else {
            throw new Error("No password or token provider configured");
        }

        // Re-read capabilities after auth (they may change)
        await this.capability();
    }

    private async starttls(): Promise<void> {
        const tag = proto.nextTag();
        const responses = await this.sendCommand(tag, proto.starttlsCommand(tag));
        const tagged = responses.find(r => r.tag === tag);
        if (!tagged || tagged.type !== "OK") throw new Error("STARTTLS failed");
        await this.transport.upgradeTLS(this.config.server);
        await this.capability();
    }

    async capability(): Promise<Set<string>> {
        const tag = proto.nextTag();
        const responses = await this.sendCommand(tag, proto.capabilityCommand(tag));
        for (const r of responses) {
            if (r.tag === "*" && r.type === "CAPABILITY") {
                this.parseCapabilities(r.text);
            }
        }
        return this.capabilities;
    }

    /** Return the cached CAPABILITY set parsed at connect/login time. Callers
     *  use this to gate optional code paths (NOTIFY, QRESYNC, MOVE, etc.)
     *  without re-issuing CAPABILITY. Returns a defensive copy so callers
     *  can't mutate internal state. */
    getCapabilities(): Set<string> {
        return new Set(this.capabilities);
    }

    /** Snapshot of the currently-SELECTed mailbox's info. Lets the compat
     *  client read `highestModSeq` etc. after any select-implying operation
     *  (fetchMessagesSinceUid, etc.) without re-issuing SELECT. */
    getMailboxInfo(): { uidValidity: number; uidNext: number; exists: number; highestModSeq?: number } {
        return {
            uidValidity: this.mailboxInfo.uidValidity,
            uidNext: this.mailboxInfo.uidNext,
            exists: this.mailboxInfo.exists,
            highestModSeq: this.mailboxInfo.highestModSeq,
        };
    }

    private parseCapabilities(text: string): void {
        const caps = text.replace(/^CAPABILITY\s*/i, "").split(/\s+/);
        this.capabilities.clear();
        for (const c of caps) this.capabilities.add(c.toUpperCase());
    }

    async logout(): Promise<void> {
        // Force-close the transport immediately. The LOGOUT command is a courtesy —
        // the server cleans up the session when the socket closes.
        // Sending LOGOUT and waiting caused hangs and connection leaks.
        this.pendingCommand = null;
        this.transport.close();
        this._connected = false;
    }

    // ── Mailbox Operations ──

    /** SELECT a mailbox. Optional QRESYNC params trigger RFC 7162 fast resync —
     *  on a re-visit the server replies with `* VANISHED (EARLIER) <uids>` for
     *  every UID expunged since the caller's last `modSeq`, plus `* FETCH`
     *  for every state change since then. The caller must have called
     *  `enable(["QRESYNC"])` once on the session for this to take effect.
     *  Returns a `SelectResult` rather than a bare `MailboxInfo` so VANISHED
     *  is delivered to the caller. The result is upcast-compatible with
     *  `MailboxInfo` for code paths that don't care about VANISHED. */
    async select(mailbox: string, qresync?: QresyncParams): Promise<SelectResult> {
        const tag = proto.nextTag();
        const responses = await this.sendCommand(tag, proto.selectCommand(tag, mailbox, qresync));
        const tagged = responses.find(r => r.tag === tag);
        if (!tagged || tagged.type !== "OK") {
            throw new Error(`SELECT ${mailbox} failed: ${tagged?.text || "unknown"}`);
        }

        // Reset transient fields each SELECT so a stale value from a previous
        // mailbox doesn't leak into the new one.
        this.mailboxInfo.highestModSeq = undefined;
        const vanishedUids: number[] = [];
        const priorUidValidity = qresync?.uidValidity;
        // Untagged FETCH responses emitted during a QRESYNC SELECT carry
        // state-change deltas the server is volunteering up-front; parse
        // them through the same path as normal FETCH so the shape matches.
        const fetchResponses = responses.filter(r => r.tag === "*" && r.type === "FETCH");

        for (const r of responses) {
            if (r.tag !== "*") continue;
            if (r.type === "EXISTS") {
                this.mailboxInfo.exists = parseInt(r.text);
            } else if (r.type === "RECENT") {
                this.mailboxInfo.recent = parseInt(r.text);
            } else if (r.type === "FLAGS") {
                this.mailboxInfo.flags = [...proto.parseFlags(r.text)];
            } else if (r.type === "OK") {
                const uidNextMatch = r.text.match(/UIDNEXT\s+(\d+)/i);
                if (uidNextMatch) this.mailboxInfo.uidNext = parseInt(uidNextMatch[1]);
                const uidValMatch = r.text.match(/UIDVALIDITY\s+(\d+)/i);
                if (uidValMatch) this.mailboxInfo.uidValidity = parseInt(uidValMatch[1]);
                const permMatch = r.text.match(/PERMANENTFLAGS\s+\(([^)]*)\)/i);
                if (permMatch) this.mailboxInfo.permanentFlags = permMatch[1].split(/\s+/).filter(Boolean);
                const modSeqMatch = r.text.match(/HIGHESTMODSEQ\s+(\d+)/i);
                if (modSeqMatch) this.mailboxInfo.highestModSeq = parseInt(modSeqMatch[1]);
            } else if (r.type === "VANISHED") {
                // `* VANISHED (EARLIER) 1:5,8,12` or `* VANISHED 1:5,8,12` —
                // strip the optional `(EARLIER)` modifier and expand the UID
                // set. EARLIER means "deleted before your last sync", which
                // is the QRESYNC case; without it, the message just vanished
                // mid-session (post-EXPUNGE on another client).
                const text = r.text.replace(/^\(EARLIER\)\s*/i, "").trim();
                // Cap expansion (see parseUidSet): a stale-modseq VANISHED range
                // can be millions of UIDs. Stop at 100k — the caller's bounded
                // reconcile takes over for an over-cap set rather than trusting
                // it (Bob 2026-06-04). 100k > any real single-cycle vanish.
                const VANISHED_EXPAND_CAP = 100_000;
                for (const uid of proto.parseUidSet(text, VANISHED_EXPAND_CAP)) vanishedUids.push(uid);
                if (vanishedUids.length >= VANISHED_EXPAND_CAP) break;
            }
        }
        this.selectedMailbox = mailbox;
        const uidValidityChanged = priorUidValidity !== undefined
            && this.mailboxInfo.uidValidity !== priorUidValidity;
        const changedMessages = fetchResponses.length > 0
            ? this.parseFetchResponses(fetchResponses)
            : [];
        return {
            ...this.mailboxInfo,
            vanishedUids,
            changedMessages,
            uidValidityChanged,
        };
    }

    /** RFC 5161 ENABLE. Activates an extension for the rest of the session.
     *  QRESYNC requires this once before any SELECT for VANISHED responses
     *  to be emitted. Capability-gate at the caller — emitting ENABLE for
     *  an unadvertised extension is a no-op on compliant servers but spams
     *  the response stream. */
    async enable(extensions: string[]): Promise<Set<string>> {
        const tag = proto.nextTag();
        const responses = await this.sendCommand(tag, proto.enableCommand(tag, extensions));
        const tagged = responses.find(r => r.tag === tag);
        if (!tagged || tagged.type !== "OK") {
            throw new Error(`ENABLE ${extensions.join(" ")} failed: ${tagged?.text || "unknown"}`);
        }
        const enabled = new Set<string>();
        for (const r of responses) {
            if (r.tag === "*" && r.type === "ENABLED") {
                for (const ext of r.text.split(/\s+/).filter(Boolean)) enabled.add(ext.toUpperCase());
            }
        }
        return enabled;
    }

    async examine(mailbox: string): Promise<MailboxInfo> {
        const tag = proto.nextTag();
        const responses = await this.sendCommand(tag, proto.examineCommand(tag, mailbox));
        const tagged = responses.find(r => r.tag === tag);
        if (!tagged || tagged.type !== "OK") {
            throw new Error(`EXAMINE ${mailbox} failed: ${tagged?.text || "unknown"}`);
        }
        for (const r of responses) {
            if (r.tag !== "*") continue;
            if (r.type === "EXISTS") this.mailboxInfo.exists = parseInt(r.text);
            else if (r.type === "RECENT") this.mailboxInfo.recent = parseInt(r.text);
        }
        this.selectedMailbox = mailbox;
        return { ...this.mailboxInfo };
    }

    /** Close the currently selected mailbox */
    async closeMailbox(): Promise<void> {
        if (!this.selectedMailbox) return;
        const tag = proto.nextTag();
        await this.sendCommand(tag, proto.buildCommand(tag, "CLOSE"));
        this.selectedMailbox = null;
    }

    // ── Folder Operations ──

    async listFolders(): Promise<NativeFolder[]> {
        const tag = proto.nextTag();
        const responses = await this.sendCommand(tag, proto.listCommand(tag));
        const folders: NativeFolder[] = [];
        let unparsed = 0;
        const listResponses = responses.filter(r => r.tag === "*" && r.type === "LIST");
        if (listResponses.length === 0 && responses.length > 0) {
            console.error(`  [imap] LIST returned ${responses.length} responses but none were LIST type. Types: ${responses.map(r => `${r.tag}:${r.type}`).join(", ")}`);
            if (responses.length <= 5) {
                for (const r of responses) console.error(`  [imap]   raw: ${JSON.stringify(r.text.substring(0, 200))}`);
            }
        }
        for (const r of responses) {
            if (r.tag === "*" && r.type === "LIST") {
                const parsed = proto.parseListResponse(r.text);
                if (parsed) {
                    folders.push(parsed);
                } else {
                    unparsed++;
                    if (unparsed <= 3) console.error(`  [imap] Unparsed LIST response: ${JSON.stringify(r.text.substring(0, 200))}`);
                }
            }
        }
        if (unparsed > 0) console.error(`  [imap] ${unparsed} LIST responses could not be parsed (${responses.length} total responses)`);
        return folders;
    }

    // 2026-09-16 — Claude Code (Fable 5.1), at Bob's direction: `items` parameter added so a
    // caller can ask for RFC 8438 SIZE. Default unchanged, so existing callers see no difference.
    async getStatus(mailbox: string, items: string[] = ["MESSAGES", "UIDNEXT", "UNSEEN"]): Promise<proto.StatusData> {
        const tag = proto.nextTag();
        const responses = await this.sendCommand(tag, proto.statusCommand(tag, mailbox, items));
        for (const r of responses) {
            if (r.tag === "*" && r.type === "STATUS") {
                const data = proto.parseStatusResponse(r.text);
                if (data) return data;
            }
        }
        return {};
    }

    /**
     * Total size of a mailbox in octets plus its message count (for imail -sizes; added
     * 2026-09-16 by Claude Code, Fable 5.1, at Bob's direction).
     *
     * Fast path: RFC 8438 `STATUS (MESSAGES SIZE)` — one round trip, no message traffic —
     * when the server advertises STATUS=SIZE (Dovecot, Gmail do).
     * Fallback: EXAMINE (read-only) + `UID FETCH 1:* (UID RFC822.SIZE)` summed here. That
     * is one FETCH line per message but no envelopes or headers, so it stays cheap even
     * on a folder of tens of thousands of messages.
     *
     * `method` says which path produced the number so a log can show it.
     */
    async getFolderSize(mailbox: string): Promise<{ messages: number; bytes: number; method: "status" | "fetch" }> {
        if (this.capabilities.has("STATUS=SIZE")) {
            const st = await this.getStatus(mailbox, ["MESSAGES", "SIZE"]);
            if (typeof st.size === "number")
                return { messages: st.messages || 0, bytes: st.size, method: "status" };
            // Server claimed STATUS=SIZE but answered without SIZE — fall through to counting.
        }

        const info = await this.examine(mailbox);
        try {
            if (!info.exists) return { messages: 0, bytes: 0, method: "fetch" };
            let messages = 0, bytes = 0;
            const tag = proto.nextTag();
            await this.sendCommand(tag, proto.fetchCommand(tag, "1:*", ["UID", "RFC822.SIZE"]), (r) => {
                if (r.tag !== "*" || r.type !== "FETCH") return;
                const m = r.text.match(/RFC822\.SIZE\s+(\d+)/);
                if (!m) return;
                messages++;
                bytes += parseInt(m[1]);
            });
            return { messages, bytes, method: "fetch" };
        }
        finally {
            await this.closeMailbox();
        }
    }

    async createMailbox(mailbox: string): Promise<void> {
        const tag = proto.nextTag();
        const responses = await this.sendCommand(tag, proto.createCommand(tag, mailbox));
        const tagged = responses.find(r => r.tag === tag);
        if (!tagged || tagged.type !== "OK") throw new Error(`CREATE failed: ${tagged?.text || "unknown"}`);
    }

    async deleteMailbox(mailbox: string): Promise<void> {
        const tag = proto.nextTag();
        const responses = await this.sendCommand(tag, proto.deleteMailboxCommand(tag, mailbox));
        const tagged = responses.find(r => r.tag === tag);
        if (!tagged || tagged.type !== "OK") throw new Error(`DELETE failed: ${tagged?.text || "unknown"}`);
    }

    async renameMailbox(from: string, to: string): Promise<void> {
        const tag = proto.nextTag();
        const responses = await this.sendCommand(tag, proto.renameCommand(tag, from, to));
        const tagged = responses.find(r => r.tag === tag);
        if (!tagged || tagged.type !== "OK") throw new Error(`RENAME failed: ${tagged?.text || "unknown"}`);
    }

    // ── Message Operations ──

    /** Fetch messages by UID range. Range may be sequence set (e.g. "1:100"), comma list ("1,5,10"), or mix ("1:10,42,50:55"). */
    async fetchMessages(range: string, options: { source?: boolean; headers?: boolean } = {}): Promise<NativeFetchedMessage[]> {
        return this.fetchMessagesStream(range, options);
    }

    /**
     * Fetch messages by UID range/list, streaming each parsed message through `onMessage`
     * as soon as its server response (including any literal bodies) is received, instead
     * of buffering the entire batch until the tagged OK. Returns all parsed messages at end.
     *
     * Pass a comma list (`"1,5,10,42"`) or sequence set (`"100:200"`) — the server handles either.
     */
    async fetchMessagesStream(
        range: string,
        options: { source?: boolean; headers?: boolean } = {},
        onMessage?: (msg: NativeFetchedMessage) => void,
    ): Promise<NativeFetchedMessage[]> {
        const items = ["UID", "FLAGS", "ENVELOPE", "RFC822.SIZE", "INTERNALDATE"];
        if (options.headers !== false) items.push("BODY.PEEK[HEADER]");
        if (options.source) items.push("BODY.PEEK[]");

        const tag = proto.nextTag();
        const streamed: NativeFetchedMessage[] = [];
        // ALWAYS parse incrementally in the per-response callback — even
        // when the caller passed no `onMessage`. The callback runs from
        // processBuffer's 10 ms time-budgeted loop, so envelope parsing of a
        // large FETCH (a sync-backfill chunk of 100+ messages) is sliced
        // across event-loop turns. The old `onMessage ? stream : batch`
        // path parsed every envelope in ONE synchronous block after the
        // tagged OK — profiled 2026-05-15 as a ~30 s daemon wedge
        // (tokenizeParenList ~40 % of CPU), starving every getMessage /
        // move / delete IPC for the duration.
        const cb = (resp: proto.ImapResponse): void => {
            if (resp.tag !== "*" || resp.type !== "FETCH") return;
            const parsed = this.parseFetchResponses([resp]);
            for (const msg of parsed) {
                streamed.push(msg);
                if (onMessage) onMessage(msg);
            }
        };

        await this.sendCommand(tag, proto.fetchCommand(tag, range, items), cb);
        return streamed;
    }

    /**
     * Folder-scoped batch body fetch. Selects `folderPath`, issues a single
     * `UID FETCH <uids> (BODY.PEEK[] ...)`, streams each body through `onBody`
     * as it arrives, and returns when the tagged OK is received.
     *
     * Eliminates per-message SELECT+FETCH round trips for callers like mailx-imap's
     * prefetch path.
     */
    async fetchBodiesBatch(
        folderPath: string,
        uids: number[],
        onBody: (uid: number, source: string) => void,
    ): Promise<void> {
        if (uids.length === 0) return;
        if (this.selectedMailbox !== folderPath) await this.select(folderPath);
        // headers default (was `headers: false`): the single-message body fetch
        // path (fetchMessage → fetchMessagesStream, headers DEFAULT) extracts
        // BODY[] correctly, but this batch path passed `headers: false` and
        // returned 0 bodies 100% of the time — so prefetch never cached anything
        // and bodies only landed when the user opened a message (Bob 2026-06-17
        // "predownloading still broken"). The only difference between the two
        // paths was this flag; matching the working path. Diagnostic counters
        // confirm extraction so a regression is visible in the log.
        let got = 0, sawResp = 0;
        await this.fetchMessagesStream(uids.join(","), { source: true }, (msg) => {
            sawResp++;
            if (msg.uid && msg.source) { got++; onBody(msg.uid, msg.source); }
        });
        if (got === 0 && uids.length > 0) {
            console.error(`  [imap] fetchBodiesBatch ${folderPath}: 0/${uids.length} bodies (saw ${sawResp} FETCH responses) — body literal not extracted`);
        }
    }

    /** Fetch messages since a UID */
    async fetchSinceUid(sinceUid: number, options: { source?: boolean } = {}, onChunk?: (msgs: NativeFetchedMessage[]) => void): Promise<NativeFetchedMessage[]> {
        const uids = await this.search(`UID ${sinceUid + 1}:*`);
        if (uids.length === 0) return [];
        uids.reverse(); // Newest first
        if (this.verbose) console.log(`  [fetch] ${uids.length} UIDs since ${sinceUid} (newest first)`);
        if (uids.length <= this.fetchChunkSize) {
            const msgs = await this.fetchMessages(uids.join(","), options);
            if (onChunk) onChunk(msgs);
            return msgs;
        }
        const allMessages: NativeFetchedMessage[] = [];
        let chunkSize = this.fetchChunkSize;
        // Advance by the chunk ACTUALLY fetched, then grow. The old
        // `i += chunkSize` used the post-growth size, so after chunk 1
        // (say 25 UIDs) the index jumped 100 — UIDs 25..99 were silently
        // skipped on every multi-chunk fetch (imail "skips some letters",
        // Bob 2026-07-17; mailx relied on set-diff sweeps to mop up).
        for (let i = 0; i < uids.length; ) {
            const chunk = uids.slice(i, i + chunkSize);
            const msgs = await this.fetchMessages(chunk.join(","), options);
            allMessages.push(...msgs);
            // console.log(`  [fetch] ${allMessages.length}/${uids.length} (chunk of ${chunk.length})`);
            if (onChunk) onChunk(msgs);
            i += chunk.length;
            if (chunkSize < this.fetchChunkSizeMax) chunkSize = Math.min(chunkSize * 4, this.fetchChunkSizeMax);
        }
        return allMessages;
    }

    /** Fetch messages by date range. Optional onChunk callback receives each batch as it arrives. */
    async fetchByDate(since: Date, before?: Date, options: { source?: boolean } = {}, onChunk?: (msgs: NativeFetchedMessage[]) => void): Promise<NativeFetchedMessage[]> {
        const criteria = proto.buildSearchCriteria({
            since,
            before: before || undefined,
        });
        // SEARCH SINCE on Dovecot can take minutes on a cold mailbox while it
        // (re)builds its date index, and unlabelled silence reads as a hang.
        // That is what the sendCommand heartbeat is for — "still waiting for
        // tag N after 30.0s — SEARCH …", unconditional and generic — so this
        // bracket does not have to fire on every call. It used to: one sync
        // pass over ~90 folders wrote two lines per folder per cycle, and on
        // Android every line is an HTTP request to the log server (Bob
        // 2026-08-29: "are we overlogging"). Verbose keeps it for debugging.
        const t0 = Date.now();
        if (this.verbose) console.log(`  [search] SINCE ${since.toISOString().slice(0, 10)}${before ? ` BEFORE ${before.toISOString().slice(0, 10)}` : ""} — running...`);
        const uids = await this.search(criteria);
        if (this.verbose) console.log(`  [search] returned ${uids.length} UIDs in ${Date.now() - t0}ms`);
        if (uids.length === 0) return [];
        // Reverse so newest messages (highest UIDs) come first
        uids.reverse();
        const allMessages: NativeFetchedMessage[] = [];
        let chunkSize = this.fetchChunkSize;
        let chunkIndex = 0;
        // Advance by the chunk ACTUALLY fetched, then grow — same skipped-
        // UIDs bug as fetchSinceUid (see comment there).
        for (let i = 0; i < uids.length; ) {
            const chunk = uids.slice(i, i + chunkSize);
            const msgs = await this.fetchMessages(chunk.join(","), options);
            allMessages.push(...msgs);
            chunkIndex++;
            // Log every 5th chunk + the first one — enough to see progress
            // without spamming. The first-chunk log proves the fetch is alive
            // even when subsequent chunks are slow.
            if (chunkIndex === 1 || chunkIndex % 5 === 0) {
                if (this.verbose) console.log(`  [fetch] ${allMessages.length}/${uids.length} messages (chunk ${chunkIndex})`);
            }
            if (onChunk) onChunk(msgs);
            i += chunk.length;
            if (chunkSize < this.fetchChunkSizeMax) chunkSize = Math.min(chunkSize * 4, this.fetchChunkSizeMax);
        }
        if (this.verbose) console.log(`  [fetch] done — ${allMessages.length} messages in ${Date.now() - t0}ms`);
        return allMessages;
    }

    /** Fetch the most recent N messages by sequence number — avoids
     *  SEARCH SINCE which can take minutes on cold Dovecot mailboxes. The
     *  selected mailbox's `EXISTS` count is used to compute the range
     *  `(EXISTS-N+1):EXISTS`; sequence FETCH is O(1) on the server because
     *  it just reads message slots, no INTERNALDATE walk. Caller must have
     *  SELECTed the mailbox first (sequence numbers are session-relative). */
    async fetchLatestN(n: number, options: { source?: boolean } = {}, onChunk?: (msgs: NativeFetchedMessage[]) => void): Promise<NativeFetchedMessage[]> {
        const exists = this.mailboxInfo.exists;
        if (!exists || exists < 1) return [];
        const start = Math.max(1, exists - n + 1);
        const range = `${start}:${exists}`;
        const t0 = Date.now();
        if (this.verbose) console.log(`  [fetch-latest] ${this.selectedMailbox || "?"}: sequence ${range} (${Math.min(n, exists)} most recent of ${exists})`);

        const items = ["UID", "FLAGS", "ENVELOPE", "RFC822.SIZE", "INTERNALDATE", "BODY.PEEK[HEADER]"];
        if (options.source) items.push("BODY.PEEK[]");

        const tag = proto.nextTag();
        const streamed: NativeFetchedMessage[] = [];
        const seenChunks = new Set<number>();
        const cb = (resp: proto.ImapResponse): void => {
            if (resp.tag !== "*" || resp.type !== "FETCH") return;
            const parsed = this.parseFetchResponses([resp]);
            for (const msg of parsed) streamed.push(msg);
            // Flush onChunk every ~50 messages without buffering the whole
            // response in memory — same shape as fetchByDate's chunk emit.
            const bucket = Math.floor(streamed.length / 50);
            if (onChunk && !seenChunks.has(bucket)) {
                seenChunks.add(bucket);
                onChunk(parsed);
            }
        };
        await this.sendCommand(tag, proto.seqFetchCommand(tag, range, items), cb);
        if (onChunk && streamed.length > 0) {
            // Final flush in case last bucket didn't trip the threshold.
            const lastBucket = Math.floor(streamed.length / 50);
            if (!seenChunks.has(lastBucket)) onChunk([]);
        }
        // Reverse so newest-first matches fetchByDate ordering — caller code
        // (mailx) expects that ordering when computing highestUid windows.
        streamed.reverse();
        if (this.verbose) console.log(`  [fetch-latest] done — ${streamed.length} messages in ${Date.now() - t0}ms`);
        return streamed;
    }

    /** Fetch a single message by UID */
    async fetchMessage(uid: number, options: { source?: boolean } = {}): Promise<NativeFetchedMessage | null> {
        const msgs = await this.fetchMessages(String(uid), options);
        return msgs.find(m => m.uid === uid) || null;
    }

    /** Get all UIDs in the current mailbox */
    async getUids(): Promise<number[]> {
        return this.search("ALL");
    }

    /** UID SEARCH */
    async search(criteria: string): Promise<number[]> {
        const tag = proto.nextTag();
        const responses = await this.sendCommand(tag, proto.searchCommand(tag, criteria));
        for (const r of responses) {
            if (r.tag === "*" && r.type === "SEARCH") {
                return proto.parseSearchResponse(r.text);
            }
        }
        // No untagged * SEARCH seen. If the tagged status is anything but OK,
        // the search FAILED — throw so callers can tell "failed" from "no
        // matches". Silently returning [] here let a failed HEADER search read
        // as "message missing from server", and mailx's sent-sweep then
        // re-APPENDed a copy every 5-minute tick — 3,704 duplicates of one
        // message over ~13 days (2026-07-02). Empty-but-OK still returns []
        // (some servers omit the * SEARCH line when there are zero hits).
        const tagged = responses.find(r => r.tag === tag);
        if (!tagged || tagged.type !== "OK") {
            throw new Error(`SEARCH failed: ${tagged ? `${tagged.type} ${tagged.text || ""}` : "no tagged response"}`);
        }
        return [];
    }

    /** Set flags on a message */
    async addFlags(uid: number, flags: string[]): Promise<void> {
        const tag = proto.nextTag();
        const responses = await this.sendCommand(tag, proto.storeCommand(tag, uid, "+FLAGS.SILENT", flags));
        const tagged = responses.find(r => r.tag === tag);
        if (!tagged || tagged.type !== "OK") throw new Error(`STORE +FLAGS failed: ${tagged?.text || "unknown"}`);
    }

    /** Remove flags from a message */
    async removeFlags(uid: number, flags: string[]): Promise<void> {
        const tag = proto.nextTag();
        const responses = await this.sendCommand(tag, proto.storeCommand(tag, uid, "-FLAGS.SILENT", flags));
        const tagged = responses.find(r => r.tag === tag);
        if (!tagged || tagged.type !== "OK") throw new Error(`STORE -FLAGS failed: ${tagged?.text || "unknown"}`);
    }

    /** Copy a message to another mailbox */
    async copyMessage(uid: number, destination: string): Promise<void> {
        const tag = proto.nextTag();
        const responses = await this.sendCommand(tag, proto.copyCommand(tag, uid, destination));
        const tagged = responses.find(r => r.tag === tag);
        if (!tagged || tagged.type !== "OK") throw new Error(`COPY failed: ${tagged?.text || "unknown"}`);
    }

    /** Move a message to another mailbox (MOVE or COPY+DELETE) */
    async moveMessage(uid: number, destination: string): Promise<void> {
        if (this.capabilities.has("MOVE")) {
            const tag = proto.nextTag();
            const responses = await this.sendCommand(tag, proto.moveCommand(tag, uid, destination));
            const tagged = responses.find(r => r.tag === tag);
            if (!tagged || tagged.type !== "OK") throw new Error(`MOVE failed: ${tagged?.text || "unknown"}`);
        } else {
            await this.copyMessage(uid, destination);
            await this.addFlags(uid, ["\\Deleted"]);
            await this.expunge();
        }
    }

    /** Delete a message by UID (flag + expunge) */
    async deleteMessage(uid: number): Promise<void> {
        await this.addFlags(uid, ["\\Deleted"]);
        await this.expunge();
    }

    /** Delete many messages in the selected mailbox: one `UID STORE <set>
     *  +FLAGS.SILENT (\Deleted)` per chunk, then a single EXPUNGE.
     *  2026-09-16 — Claude Code (Fable 5.1), at Bob's direction. Emptying
     *  Trash looped deleteMessage() per UID — a STORE and an EXPUNGE round
     *  trip for every message — so a large Trash took minutes and the
     *  caller's IPC timed out ("mailxapi timeout: emptyFolder"). Chunked so a
     *  ten-thousand-UID set never produces a command line the server rejects. */
    async deleteMessages(uids: number[], chunkSize = 1000): Promise<void> {
        if (!uids.length) return;
        for (let i = 0; i < uids.length; i += chunkSize) {
            const set = proto.uidSequenceSet(uids.slice(i, i + chunkSize));
            const tag = proto.nextTag();
            const responses = await this.sendCommand(tag, proto.storeCommand(tag, set, "+FLAGS.SILENT", ["\\Deleted"]));
            const tagged = responses.find(r => r.tag === tag);
            if (!tagged || tagged.type !== "OK") throw new Error(`STORE +FLAGS \\Deleted (${uids.length} uids) failed: ${tagged?.text || "unknown"}`);
        }
        await this.expunge();
    }

    /** Expunge deleted messages */
    async expunge(): Promise<void> {
        const tag = proto.nextTag();
        await this.sendCommand(tag, proto.buildCommand(tag, "EXPUNGE"));
    }

    /** Append a message to a mailbox */
    async appendMessage(mailbox: string, message: string | Uint8Array, flags: string[] = []): Promise<number | null> {
        const data = typeof message === "string" ? message : new TextDecoder().decode(message);
        const size = new TextEncoder().encode(data).length;

        const tag = proto.nextTag();
        const cmd = proto.appendCommand(tag, mailbox, flags, size);

        // Send command, wait for continuation
        await this.transport.write(cmd);

        // Wait for "+" continuation
        const contResp = await this.waitForContinuation(tag);
        if (!contResp) throw new Error("APPEND: server did not send continuation");

        // Send the message data + CRLF
        await this.transport.write(data + "\r\n");

        // Wait for tagged response
        const responses = await this.waitForTagged(tag);
        const tagged = responses.find(r => r.tag === tag);
        if (!tagged || tagged.type !== "OK") throw new Error(`APPEND failed: ${tagged?.text || "unknown"}`);

        // Try to extract APPENDUID
        const uidMatch = tagged.text.match(/APPENDUID\s+\d+\s+(\d+)/i);
        return uidMatch ? parseInt(uidMatch[1]) : null;
    }

    // ── NOTIFY (RFC 5465) ──

    /** Issue NOTIFY SET so the server starts pushing unsolicited STATUS
     *  responses for the mailboxes named in `spec` (beyond the currently
     *  selected one). Capability gated — callers must check
     *  `getCapabilities().has("NOTIFY")` before calling. Set
     *  `onMailboxStatus` before issuing if you want to react. Issue AFTER
     *  SELECT and BEFORE startIdle — the server holds the spec for the
     *  lifetime of the connection. */
    async notify(spec: string): Promise<void> {
        const tag = proto.nextTag();
        const responses = await this.sendCommand(tag, proto.notifyCommand(tag, spec));
        const tagged = responses.find(r => r.tag === tag);
        if (!tagged || tagged.type !== "OK") {
            throw new Error(`NOTIFY SET failed: ${tagged?.text || "unknown"}`);
        }
    }

    // ── IDLE ──

    async startIdle(
        onNewMail: (count: number) => void,
        onExpunge?: () => void,
        onFlagChange?: (seq: number, flags: string[]) => void,
    ): Promise<() => Promise<void>> {
        this.idleCallback = onNewMail;
        this.idleExpungeCallback = onExpunge ?? null;
        this.idleFlagCallback = onFlagChange ?? null;
        this.idleStopped = false;

        const beginIdleCycle = async (): Promise<void> => {
            const tag = proto.nextTag();
            this.idleTag = tag;
            // Arm continuation listener BEFORE writing — some servers reply fast enough
            // that data can arrive in the same tick.
            const waitCont = this.waitForContinuation(tag);
            await this.transport.write(proto.idleCommand(tag));
            await waitCont;
            // RFC 2177: re-IDLE every ~28 minutes to avoid servers that terminate
            // idle sessions at the 29-minute mark, and to give us a steady
            // application-level keepalive that detects silently-dropped sockets.
            this.idleRefreshTimer = setTimeout(() => {
                if (this.idleStopped) return;
                this.refreshIdle().catch(err => {
                    if (this.verbose) console.error(`  [imap] IDLE refresh failed: ${err.message}`);
                });
            }, 28 * 60 * 1000);
        };

        await beginIdleCycle();

        return async () => {
            this.idleStopped = true;
            if (this.idleRefreshTimer) { clearTimeout(this.idleRefreshTimer); this.idleRefreshTimer = null; }
            const tag = this.idleTag;
            this.idleTag = null;
            this.idleCallback = null;
            this.idleExpungeCallback = null;
            if (tag) {
                // Arm tagged-wait BEFORE DONE — avoid missing the OK.
                const waitTag = this.waitForTagged(tag);
                try { await this.transport.write(proto.doneCommand()); } catch { /* ignore */ }
                try { await waitTag; } catch { /* ignore */ }
            }
        };
    }

    /**
     * If IDLE is currently active, send DONE and wait for its tagged OK so the
     * connection is free to accept a new command. Saves the active callback so
     * the companion `resumeIdleAfterCommand` can re-enter IDLE on the same
     * mailbox afterwards. Returns the saved state, or null if IDLE was not active.
     *
     * Called automatically at the top of `sendCommand` whenever the caller issues
     * an interleaved command on an IDLE-holding connection (e.g. pullMail reacting
     * to an EXISTS notification).
     */
    private async suspendIdleForCommand(): Promise<{ callback: (count: number) => void } | null> {
        const oldTag = this.idleTag;
        const cb = this.idleCallback;
        if (!oldTag || !cb) return null;
        if (this.idleRefreshTimer) { clearTimeout(this.idleRefreshTimer); this.idleRefreshTimer = null; }
        this.idleTag = null;
        this.idleCallback = null;
        if (this.verbose) console.log(`  [imap] IDLE auto-suspending for interleaved command`);
        // Arm tagged listener BEFORE DONE so we don't miss the OK if the server is fast.
        const waitTag = this.waitForTagged(oldTag);
        try { await this.transport.write(proto.doneCommand()); }
        catch (err) {
            // Write failure — pendingCommand is still set by waitForTagged; clear it to prevent hang.
            this.pendingCommand = null;
            throw err;
        }
        try { await waitTag; } catch { /* best-effort — proceed even if OK was lost */ }
        return { callback: cb };
    }

    /** Re-enter IDLE with the saved callback after an auto-suspended command completes. */
    private async resumeIdleAfterCommand(info: { callback: (count: number) => void }): Promise<void> {
        // The caller may have stopped IDLE during the command window — respect that.
        if (this.idleStopped) return;
        const newTag = proto.nextTag();
        this.idleTag = newTag;
        this.idleCallback = info.callback;
        // Arm continuation listener BEFORE writing IDLE.
        const waitCont = this.waitForContinuation(newTag);
        await this.transport.write(proto.idleCommand(newTag));
        await waitCont;
        if (this.verbose) console.log(`  [imap] IDLE auto-resumed (${newTag})`);
        this.idleRefreshTimer = setTimeout(() => {
            if (this.idleStopped) return;
            this.refreshIdle().catch(err => {
                if (this.verbose) console.error(`  [imap] IDLE refresh failed: ${err.message}`);
            });
        }, 28 * 60 * 1000);
    }

    /** Send DONE + re-IDLE. Called by the 28-minute refresh timer. */
    private async refreshIdle(): Promise<void> {
        const oldTag = this.idleTag;
        if (!oldTag || !this.idleCallback) return;
        const cb = this.idleCallback;
        // Arm tagged listener BEFORE writing DONE to avoid missing a fast OK.
        const waitTag = this.waitForTagged(oldTag);
        await this.transport.write(proto.doneCommand());
        try { await waitTag; } catch { /* best-effort */ }
        // Re-IDLE with a new tag
        this.idleCallback = cb; // waitForTagged resolved pendingCommand, reinstall callback
        const newTag = proto.nextTag();
        this.idleTag = newTag;
        const waitCont = this.waitForContinuation(newTag);
        await this.transport.write(proto.idleCommand(newTag));
        await waitCont;
        if (this.verbose) console.log(`  [imap] IDLE refreshed (${newTag})`);
        // Schedule the next refresh
        this.idleRefreshTimer = setTimeout(() => {
            if (this.idleStopped) return;
            this.refreshIdle().catch(err => {
                if (this.verbose) console.error(`  [imap] IDLE refresh failed: ${err.message}`);
            });
        }, 28 * 60 * 1000);
    }

    // ── Message count (lightweight STATUS) ──

    async getMessageCount(mailbox: string): Promise<number> {
        const status = await this.getStatus(mailbox);
        return status.messages || 0;
    }

    // ── Low-level command handling ──

    /** Inactivity timeout — how long to wait with NO data before declaring the connection dead.
     *  This is NOT a wall-clock timeout. Timer resets every time data arrives from the server.
     *  A large FETCH returning data continuously will never timeout.
     *  60s accommodates Gmail which is slow on SEARCH for large folders.
     *  Overridable via ImapClientConfig.inactivityTimeout — slow Dovecot servers need 180s+. */
    private inactivityTimeout: number;
    /** Hard wall-clock cap per command (independent of inactivityTimeout).
     *  A pathological server that returns one byte every (inactivityTimeout-1)
     *  seconds would otherwise defer the inactivity timer forever; this is
     *  the absolute deadline that always fires. Default 5 minutes. */
    private commandWallClockTimeout: number;
    /** Server-greeting timeout in ms — how long to wait for the server's
     *  initial banner after TCP/TLS connects. Default 10s; bumped to 30s
     *  for slow shared-hosting Dovecot in mailx-imap. */
    private greetingTimeout: number;

    /** Fetch chunk sizes — start small for quick first paint, ramp up for throughput.
     *  Default 25 initial → 500 max. Overridable via ImapClientConfig.fetchChunkSize /
     *  fetchChunkSizeMax. Slow servers benefit from smaller chunks (fewer timeouts). */
    private fetchChunkSize: number;
    private fetchChunkSizeMax: number;

    /** Active command timer — reset by handleData on every data arrival */
    private commandTimer: ReturnType<typeof setTimeout> | null = null;

    /**
     * Issue an IMAP command and await its tagged response.
     *
     * If IDLE is currently active on this connection, DONE it first (and wait
     * for its tagged OK) before writing the new command; re-enter IDLE after
     * the command finishes. Without this, commands issued from within an IDLE
     * EXISTS callback (e.g. mailpuller's pullMail) would be ignored by the
     * server until the inactivity timer killed the socket.
     */
    /** Per-client command serialization. IMAP allows only one command in
     *  flight at a time on a single connection (RFC 3501 §2.2.1). The
     *  client's `pendingCommand` field tracks that command — if a second
     *  caller concurrently invokes sendCommand, it overwrites
     *  pendingCommand and the first caller's await is orphaned. We saw
     *  this in production: the outbox poller's `withConnection`-queued
     *  task ran on the same shared ops client as syncFolder's INBOX work,
     *  and INBOX silently hung forever (no heartbeat fire, no wall-clock,
     *  no reject — the promise just had no command tracking it anymore).
     *
     *  This chain serializes ALL sendCommand callers against this client
     *  regardless of whether the caller bothered to coordinate.
     *  Defensive — the right thing for the upper layer to do is also use
     *  the queue (withConnection in mailx-imap), but the IMAP client
     *  shouldn't allow itself to be corrupted by a careless caller. */
    private commandChain: Promise<unknown> = Promise.resolve();
    private async sendCommand(
        tag: string,
        command: string,
        onUntagged?: (resp: proto.ImapResponse) => void,
    ): Promise<proto.ImapResponse[]> {
        const prev = this.commandChain.catch(() => { /* ignore predecessor errors */ });
        let myResolve!: (v: proto.ImapResponse[]) => void;
        let myReject!: (e: any) => void;
        const myPromise = new Promise<proto.ImapResponse[]>((res, rej) => { myResolve = res; myReject = rej; });
        this.commandChain = myPromise.catch(() => { /* */ });
        // Phase timing — multi-session "62 s INBOX stall" hunt. The stall
        // produces NO `>` log (sendCommandCore never runs), so it lives in
        // one of the two awaits below. Logging only slow phases (>1 s, Bob's
        // "synchronous shouldn't span" threshold lifted to network scale)
        // pins which one without spamming the normal fast path.
        const __cmdName = command.split(/[\s\r]/)[0] || command.slice(0, 12);
        const __p0 = Date.now();
        await prev;
        const __prevMs = Date.now() - __p0;
        if (__prevMs > 1000) console.warn(`  [imap] cmd-chain wait ${__prevMs}ms before ${__cmdName} (predecessor held the connection)`);
        try {
            const __p1 = Date.now();
            const idleState = await this.suspendIdleForCommand();
            const __idleMs = Date.now() - __p1;
            if (__idleMs > 1000) console.warn(`  [imap] IDLE-suspend wait ${__idleMs}ms before ${__cmdName}`);
            try {
                const out = await this.sendCommandCore(tag, command, onUntagged);
                myResolve(out);
                return out;
            } finally {
                if (idleState) {
                    try { await this.resumeIdleAfterCommand(idleState); }
                    catch (err) { if (this.verbose) console.error(`  [imap] IDLE auto-resume failed: ${(err as Error).message}`); }
                }
            }
        } catch (e) {
            myReject(e);
            throw e;
        }
    }

    private sendCommandCore(
        tag: string,
        command: string,
        onUntagged?: (resp: proto.ImapResponse) => void,
    ): Promise<proto.ImapResponse[]> {
        return new Promise((resolve, reject) => {
            if (this.verbose && !command.includes("LOGIN") && !command.includes("AUTHENTICATE")) {
                console.log(`  [imap] > ${command.trimEnd()}`);
            }

            // Snapshot transport diagnostics — used by both heartbeat and timeout.
            const transportDiag = (): string => {
                try {
                    const d = (this.transport as any)?.diagnostics;
                    if (!d) return "";
                    const sinceRead = d.lastReadAt ? Date.now() - d.lastReadAt : -1;
                    return ` [conn#${d.connId} r=${d.bytesRead}B w=${d.bytesWritten}B writes=${d.writeCount} sinceLastRead=${sinceRead}ms]`;
                } catch { return ""; }
            };

            // Always start clean — defends against a prior pendingCommand
            // having leaked timers (e.g. swap by suspendIdle/resumeIdle that
            // bypassed resolve/reject).
            this.clearAllCommandTimers();

            const cmdStart = Date.now();
            const cmdSummary = command.split("\r")[0].substring(0, 80);

            const onTimeout = (kind: string): void => {
                this.clearAllCommandTimers();
                this.pendingCommand = null;
                this._connected = false;
                this.transport.close?.();
                reject(new Error(`${kind}: ${cmdSummary}${transportDiag()}`));
            };

            // Three timers, three responsibilities — all instance-scoped now:
            //   commandTimer  = inactivity (resets on each data chunk)
            //   wallClockTimer = hard cap from command start, never resets
            //   heartbeatTimer = periodic progress log every 30s
            this.commandTimer = setTimeout(
                () => onTimeout(`IMAP inactivity timeout (${this.inactivityTimeout / 1000}s)`),
                this.inactivityTimeout,
            );
            this.wallClockTimer = setTimeout(
                () => onTimeout(`IMAP command wall-clock timeout (${this.commandWallClockTimeout / 1000}s)`),
                this.commandWallClockTimeout,
            );
            this.heartbeatTimer = setInterval(() => {
                const waited = Date.now() - cmdStart;
                console.log(`  [imap] still waiting for tag ${tag} after ${(waited / 1000).toFixed(1)}s — ${cmdSummary}${transportDiag()}`);
            }, 30_000);

            this.pendingCommand = {
                tag, responses: [],
                resolve: (responses) => { this.clearAllCommandTimers(); resolve(responses); },
                reject: (err) => { this.clearAllCommandTimers(); reject(err); },
                onUntagged,
            };
            // PRE-WRITE HEALTH CHECK — verify the socket is in a writable
            // state BEFORE we burn the inactivityTimeout window waiting for
            // a reply that can't come. socket-half-closed (peer FIN'd) and
            // destroyed states both fail fast here instead of after 300s.
            try {
                const h = (this.transport as any)?.socketHealth;
                if (h && (h.destroyed || h.writableEnded || !h.writable)) {
                    this.clearAllCommandTimers();
                    this.pendingCommand = null;
                    this._connected = false;
                    reject(new Error(`IMAP socket dead before write: destroyed=${h.destroyed} writableEnded=${h.writableEnded} writable=${h.writable}${transportDiag()}`));
                    return;
                }
            } catch { /* transport may not expose socketHealth — fall through */ }

            this.transport.write(command).catch((err) => {
                this.clearAllCommandTimers();
                // Write failure = dead socket. Clear pendingCommand + disconnect flag
                // so the next ensureConnected() does a fresh connect instead of
                // reusing a socket that the OS layer has already declared dead
                // (the source of the "hostname nor servname provided" cascade).
                this.pendingCommand = null;
                this._connected = false;
                reject(err);
            });
        });
    }

    private waitForContinuation(tag: string): Promise<proto.ImapResponse | null> {
        return new Promise((resolve) => {
            const timeout = setTimeout(() => {
                this.continuationResolve = null;
                resolve(null);
            }, 30000);
            this.continuationResolve = (resp) => {
                clearTimeout(timeout);
                this.continuationResolve = null;
                resolve(resp);
            };
        });
    }

    private waitForTagged(tag: string): Promise<proto.ImapResponse[]> {
        return new Promise((resolve, reject) => {
            this.pendingCommand = { tag, resolve, reject, responses: [] };
        });
    }

    private handleData(data: Uint8Array): void {
        // Reset inactivity timer ONLY (heartbeat + wall-clock NEVER reset
        // on data — that was the original bug: wall-clock got reset every
        // byte, so a slow-trickling server's command never died). Inactivity
        // is "no data for N seconds"; data IS arriving, so push it out.
        if (this.commandTimer) {
            clearTimeout(this.commandTimer);
            this.commandTimer = setTimeout(() => {
                this.commandTimer = null;
                if (this.pendingCommand) {
                    const cmd = this.pendingCommand;
                    this.pendingCommand = null;
                    // Use the canonical clear so heartbeat + wall-clock
                    // also stop. cmd.reject runs clearAllCommandTimers via
                    // the closure binding — call it directly here too in
                    // case cmd.reject was already replaced (defense in depth).
                    this.clearAllCommandTimers();
                    this._connected = false;
                    this.transport.close?.();
                    cmd.reject(new Error(`IMAP inactivity timeout (${this.inactivityTimeout / 1000}s)`));
                }
            }, this.inactivityTimeout);
        }
        this.appendToBuffer(data);
        this.processBuffer();
    }

    /** Append received bytes to the receive buffer, growing capacity if
     *  needed. O(n) over the chunk size, never over the buffer total —
     *  doubling growth amortizes to O(1) per byte across the response. */
    private appendToBuffer(data: Uint8Array): void {
        const need = this.bufferLength + data.length;
        if (need > this.buffer.length) {
            let cap = Math.max(this.buffer.length * 2, 8192);
            while (cap < need) cap *= 2;
            const grown = new Uint8Array(cap);
            grown.set(this.buffer.subarray(0, this.bufferLength));
            this.buffer = grown;
        }
        this.buffer.set(data, this.bufferLength);
        this.bufferLength += data.length;
    }

    /** Single canonical "command finished or abandoned" cleanup. Every
     *  path that ends a command's lifecycle — resolve, reject, timeout,
     *  socket close, command-replace — calls this. Was a leak source:
     *  closure-scoped timers in sendCommandCore couldn't be cleared from
     *  handleData's inline-replacement timer or from IDLE suspend/resume. */
    private clearAllCommandTimers(): void {
        if (this.commandTimer) { clearTimeout(this.commandTimer); this.commandTimer = null; }
        if (this.heartbeatTimer) { clearInterval(this.heartbeatTimer); this.heartbeatTimer = null; }
        if (this.wallClockTimer) { clearTimeout(this.wallClockTimer); this.wallClockTimer = null; }
    }

    /** Compact the buffer when bufferOffset has advanced past half the used
     *  region. copyWithin moves the unread bytes to the front in-place
     *  (no new allocation). Amortized O(1) per byte; never the O(n²)
     *  substring-each-line pathology that blocked the event loop on a
     *  large LIST response. */
    private compactBufferIfNeeded(): void {
        if (this.bufferOffset > 0 && this.bufferOffset >= this.bufferLength / 2) {
            this.buffer.copyWithin(0, this.bufferOffset, this.bufferLength);
            this.bufferLength -= this.bufferOffset;
            this.bufferOffset = 0;
        }
    }

    /** Find the next CRLF (0x0D 0x0A) in the unread region, returning the
     *  byte index of the 0x0D, or -1 if not found. Manual byte loop —
     *  Uint8Array doesn't have a native indexOf for byte sequences. */
    private indexOfCRLF(from: number): number {
        const buf = this.buffer;
        const end = this.bufferLength - 1;
        for (let i = from; i < end; i++) {
            if (buf[i] === 0x0D && buf[i + 1] === 0x0A) return i;
        }
        return -1;
    }

    private processBuffer(): void {
        // Time-budget the synchronous processing so a large FETCH burst
        // (sync of many envelopes in a single data chunk) doesn't starve
        // the Node event loop. Without this yield, IPC dispatchers in the
        // same process can wait 10+ seconds to get cycles — visible as
        // `mailxapi timeout: getMessage` / `getUnifiedInbox` on the desktop
        // client during heavy sync (Bob 2026-05-15: log shows daemon doing
        // hundreds of literal-consumed per second; client IPCs starve and
        // hit 30s ceiling). 10 ms budget = the same time slice Node uses
        // for I/O; after that we yield via setImmediate so timers, IPC,
        // and other I/O get a turn, then resume.
        const t0 = performance.now();
        const TIME_BUDGET_MS = 10;
        while (true) {
            if (performance.now() - t0 > TIME_BUDGET_MS) {
                yieldToEventLoop(() => this.processBuffer());
                return;
            }
            // Check for literal — `{N}` announces N octets of arbitrary data
            // (message body, MIME part, attachment chunk). The buffer is
            // bytes; the check is a direct length compare and the slice is
            // O(N) over the literal's own size — never O(buffer²).
            if (this.pendingCommand?.literalBytes != null) {
                const neededBytes = this.pendingCommand.literalBytes;
                const available = this.bufferLength - this.bufferOffset;
                if (available < neededBytes) {
                    if (this.verbose && this.pendingCommand.literalBytes > 0) {
                        console.log(`  [imap] waiting for literal: need ${neededBytes} bytes, have ${available}`);
                    }
                    break; // Wait for more data — cheap, no decode.
                }
                // Slice the literal bytes; advance the read cursor by N.
                // The bytes themselves stay in the underlying buffer for the
                // moment, but bufferOffset advances past them and the next
                // compactBufferIfNeeded will reclaim the space.
                const literalBytes = this.buffer.subarray(this.bufferOffset, this.bufferOffset + neededBytes);
                this.bufferOffset += neededBytes;
                // Decode literal once (UTF-8 with replacement). For BODY[]
                // literals carrying non-UTF-8 charsets this is still lossy —
                // the proper fix is to keep literals as bytes through to the
                // body store, tracked separately. The win HERE is that the
                // decode happens once per literal, not once per chunk that
                // arrives during the literal's transit.
                let literal = this.utf8Decoder.decode(literalBytes);
                // DESYNC CAPTURE (Bob 2026-06-16, overnight debug). A BODY[]
                // literal whose content is itself an IMAP command tag means the
                // byte-buffer parser misaligned: the declared {N} didn't match
                // reality and we swallowed a later command's bytes. Dump the
                // declared size + the misaligned content + the raw bytes that
                // PRECEDED the literal, so the mechanism (how the {N} boundary
                // slipped) is finally visible. Cheap; only fires on the desync.
                if (this.pendingCommand.currentLiteralKey?.startsWith("BODY")
                    && /^[A-Za-z]+\d+\s+(SELECT|FETCH|UID|LOGIN|STATUS|EXAMINE|LIST|IDLE|NOOP|STORE|SEARCH|CAPABILITY|CLOSE|LOGOUT|APPEND|COPY|MOVE|EXPUNGE|CREATE|DELETE|RENAME|SUBSCRIBE|ENABLE|STARTTLS)\b/i.test(literal.slice(0, 80).replace(/^[\s*]+/, ""))) {
                    const litStart = this.bufferOffset - neededBytes;
                    const ctxStart = Math.max(0, litStart - 256);
                    const ctx = this.buffer.subarray(ctxStart, litStart);
                    const ascii = Array.from(ctx).map(b => (b >= 32 && b < 127) ? String.fromCharCode(b) : (b === 13 ? "\\r" : b === 10 ? "\\n" : ".")).join("");
                    console.error(`  [imap] DESYNC CAPTURE key=${this.pendingCommand.currentLiteralKey} declaredLiteral=${neededBytes}B bufferOffset=${this.bufferOffset} bufferLength=${this.bufferLength} litStart=${litStart}`);
                    console.error(`  [imap] DESYNC literal[0..200]=${JSON.stringify(literal.slice(0, 200))}`);
                    console.error(`  [imap] DESYNC preceding[${ctx.length}B]=${JSON.stringify(ascii)}`);
                }
                // For non-BODY literals (e.g. display names in ENVELOPE), wrap in quotes
                // so tokenizeParenList treats them as a single token
                if (!this.pendingCommand.currentLiteralKey) {
                    literal = `"${literal.replace(/\\/g, "\\\\").replace(/"/g, '\\"').replace(/\r?\n/g, "")}"`;
                }
                this.pendingCommand.literalBuffer = (this.pendingCommand.literalBuffer || "") + literal;
                this.pendingCommand.literalBytes = undefined;
                // Store the literal data by its BODY key for parseFetchResponses
                if (this.pendingCommand.currentLiteralKey) {
                    if (!this.pendingCommand.literals) this.pendingCommand.literals = new Map();
                    this.pendingCommand.literals.set(this.pendingCommand.currentLiteralKey, literal);
                    this.pendingCommand.currentLiteralKey = undefined;
                    this.pendingCommand.currentLiteralSize = undefined;
                }
                if (this.verbose) console.log(`  [imap] literal consumed, ${neededBytes} bytes, buffer remaining: ${this.bufferLength - this.bufferOffset}`);
                // Allow the consumed-prefix to be reclaimed when half-full —
                // long literal-heavy responses would otherwise grow the
                // backing buffer indefinitely between commands.
                this.compactBufferIfNeeded();
                continue;
            }

            // Find next CRLF starting from the read offset on bytes (not
            // chars). bufferOffset advances past consumed bytes; we only
            // search the unread region so per-line work is O(line length),
            // never O(response size).
            const lineEnd = this.indexOfCRLF(this.bufferOffset);
            if (lineEnd < 0) {
                // No complete line yet. Compact the buffer if we've advanced
                // far enough that the unconsumed-prefix is dragging.
                this.compactBufferIfNeeded();
                break;
            }

            // Slice the line bytes (including trailing CRLF) and decode
            // once. IMAP control lines are 7-bit ASCII per RFC 3501; UTF-8
            // decode is a superset and produces the same characters. The
            // existing string-based parser (parseResponseLine, tokenizers,
            // ENVELOPE/FLAGS handling) consumes the decoded line below.
            const lineBytes = this.buffer.subarray(this.bufferOffset, lineEnd + 2);
            const line = this.utf8Decoder.decode(lineBytes);
            this.bufferOffset = lineEnd + 2;
            // Periodic compaction so the consumed prefix doesn't keep
            // growing forever in long-running connections (IDLE, big sync).
            this.compactBufferIfNeeded();

            // Check for literal announcement {size}\r\n at end of line
            const literalMatch = line.match(/\{(\d+)\}\r\n$/);
            if (literalMatch && this.pendingCommand) {
                const size = parseInt(literalMatch[1]);
                // Store the line prefix before {size} — will be prepended after literal is consumed
                const linePrefix = line.substring(0, literalMatch.index);
                if (this.pendingCommand.literalBuffer) {
                    // Already have buffered data from a previous literal — append this prefix
                    this.pendingCommand.literalBuffer += linePrefix;
                } else {
                    this.pendingCommand.literalBuffer = linePrefix;
                }
                this.pendingCommand.literalBytes = size;
                // Extract the BODY section key from the prefix (e.g. BODY[], BODY[HEADER], BODY.PEEK[])
                const keyMatch = linePrefix.match(/BODY(?:\.PEEK)?\[([^\]]*)\]\s*$/i);
                if (keyMatch) {
                    this.pendingCommand.currentLiteralKey = `BODY[${keyMatch[1]}]`;
                    this.pendingCommand.currentLiteralSize = size;
                }
                if (this.verbose) console.log(`  [imap] literal announced: ${size} bytes, prefix: ${linePrefix.length} chars, key: ${this.pendingCommand.currentLiteralKey || "none"}`);
                continue;
            }

            // If we have buffered literal data, prepend it to this line (the closing part after the literal)
            let fullLine = line;
            if (this.pendingCommand?.literalBuffer) {
                fullLine = this.pendingCommand.literalBuffer + line;
                this.pendingCommand.literalBuffer = undefined;
            }

            const resp = proto.parseResponseLine(fullLine);

            // Attach accumulated literals to the response and reset for next response
            if (this.pendingCommand?.literals?.size) {
                resp.literals = this.pendingCommand.literals;
                this.pendingCommand.literals = undefined;
            }

            if (this.verbose) {
                const display = resp.raw.length > 200 ? resp.raw.substring(0, 200) + `... (${resp.raw.length} bytes)` : resp.raw;
                console.log(`  [imap] < [tag=${resp.tag} type=${resp.type}] ${display}`);
            }

            // Server greeting — resolve readGreeting() promise
            if (this.greetingResolve && resp.tag === "*" && (resp.type === "OK" || resp.type === "PREAUTH")) {
                const resolve = this.greetingResolve;
                this.greetingResolve = null;
                resolve(resp);
                continue;
            }

            // During IDLE, handle EXISTS notifications
            if (this.idleTag && resp.tag === "*" && resp.type === "EXISTS") {
                const count = parseInt(resp.text);
                if (this.idleCallback && count > this.mailboxInfo.exists) {
                    const newCount = count - this.mailboxInfo.exists;
                    this.mailboxInfo.exists = count;
                    this.idleCallback(newCount);
                } else if (count < this.mailboxInfo.exists) {
                    // Count dropped — a message was expunged on the selected
                    // mailbox (another client / Thunderbird deleted it).
                    // Signal the caller to reconcile the deletion now.
                    this.mailboxInfo.exists = count;
                    if (this.idleExpungeCallback) this.idleExpungeCallback();
                }
                continue;
            }

            // During IDLE, an unsolicited `* n EXPUNGE` / `* VANISHED` for the
            // selected mailbox (RFC 5465 NOTIFY SELECTED MessageExpunge, or a
            // plain post-EXPUNGE notification). Push a deletion reconcile.
            if (this.idleTag && resp.tag === "*" && (resp.type === "EXPUNGE" || resp.type === "VANISHED")) {
                if (this.mailboxInfo.exists > 0) this.mailboxInfo.exists--;
                if (this.idleExpungeCallback) this.idleExpungeCallback();
                continue;
            }

            // A flag changed in the SELECTED mailbox: the server sends an
            // unsolicited "* 1234 FETCH (FLAGS (\Seen \Flagged))" — this is
            // how starring a message on a phone reaches another client. It fell
            // through here and was dropped, so the desktop only learned about
            // it on its next periodic sync of that folder: measured on Bob's
            // own log, a median of 5 minutes and up to 20 (2026-08-29).
            if (this.idleTag && resp.tag === "*" && resp.type === "FETCH" && /FLAGS\s*\(/i.test(resp.text)) {
                if (this.idleFlagCallback) {
                    const seq = parseInt(resp.text, 10);
                    const inner = resp.text.match(/FLAGS\s*\(([^)]*)\)/i)?.[1] || "";
                    const flags = inner.split(/\s+/).filter(Boolean);
                    try { this.idleFlagCallback(Number.isFinite(seq) ? seq : 0, flags); }
                    catch (err) { if (this.verbose) console.error(`  [imap] onFlagChange threw: ${(err as Error).message}`); }
                }
                continue;
            }

            // RFC 5465 NOTIFY: unsolicited STATUS responses for non-selected
            // mailboxes arrive when the server has accepted a NOTIFY SET that
            // included a PERSONAL (or other) event group. Only route to the
            // callback when IDLE is active — a STATUS response outside IDLE
            // is the result of a direct STATUS command and belongs to the
            // pending command's response set (the fall-through below).
            if (this.idleTag && resp.tag === "*" && resp.type === "STATUS" && this.onMailboxStatus) {
                const parsed = proto.parseStatusResponseFull(resp.text);
                if (parsed) {
                    try { this.onMailboxStatus(parsed.mailbox, parsed.data); }
                    catch (err) { if (this.verbose) console.error(`  [imap] onMailboxStatus threw: ${(err as Error).message}`); }
                    continue;
                }
                // Parse failed — fall through so the response isn't silently dropped.
            }

            // Collect untagged responses for the pending command
            if (resp.tag === "*" && this.pendingCommand) {
                this.pendingCommand.responses.push(resp);
                if (this.pendingCommand.onUntagged) {
                    try { this.pendingCommand.onUntagged(resp); }
                    catch (err) { if (this.verbose) console.error(`  [imap] onUntagged callback threw: ${(err as Error).message}`); }
                }
                continue;
            }

            // Continuation response
            if (resp.tag === "+") {
                if (this.continuationResolve) {
                    // APPEND or other command waiting for continuation
                    this.continuationResolve(resp);
                } else if (!this.idleTag && this.pendingCommand) {
                    // Unexpected continuation (e.g. AUTHENTICATE challenge) — cancel
                    this.transport.write("\r\n").catch(() => {});
                }
                continue;
            }

            // Tagged response — command complete
            if (this.pendingCommand && resp.tag === this.pendingCommand.tag) {
                this.pendingCommand.responses.push(resp);
                const { resolve, responses } = this.pendingCommand;
                this.pendingCommand = null;
                resolve(responses);
                continue;
            }

            // Unhandled
            this.handleUntaggedResponse(resp);
        }
    }

    private handleUntaggedResponse(resp: proto.ImapResponse): void {
        if (resp.type === "EXISTS") {
            this.mailboxInfo.exists = parseInt(resp.text);
        } else if (resp.type === "EXPUNGE") {
            this.mailboxInfo.exists = Math.max(0, this.mailboxInfo.exists - 1);
        }
    }

    // ── FETCH Response Parser ──

    private parseFetchResponses(responses: proto.ImapResponse[]): NativeFetchedMessage[] {
        const messages: NativeFetchedMessage[] = [];

        for (const r of responses) {
            if (r.tag !== "*" || r.type !== "FETCH") continue;

            const msg: NativeFetchedMessage = {
                seq: 0, uid: 0, flags: new Set(), date: null,
                subject: "", messageId: "", from: [], to: [], cc: [], bcc: [],
                sender: [], replyTo: [], inReplyTo: "", size: 0,
                source: "", headers: "", seen: false, flagged: false,
                answered: false, draft: false,
            };

            // Extract sequence number from "5 FETCH (...)"
            const seqMatch = r.text.match(/^(\d+)\s+FETCH/);
            if (seqMatch) msg.seq = parseInt(seqMatch[1]);

            // Extract UID
            const uidMatch = r.text.match(/UID\s+(\d+)/);
            if (uidMatch) msg.uid = parseInt(uidMatch[1]);

            // Extract FLAGS
            const flagsMatch = r.text.match(/FLAGS\s+\(([^)]*)\)/);
            if (flagsMatch) {
                msg.flags = new Set(flagsMatch[1].split(/\s+/).filter(Boolean));
                msg.seen = msg.flags.has("\\Seen");
                msg.flagged = msg.flags.has("\\Flagged");
                msg.answered = msg.flags.has("\\Answered");
                msg.draft = msg.flags.has("\\Draft");
            }

            // Extract RFC822.SIZE
            const sizeMatch = r.text.match(/RFC822\.SIZE\s+(\d+)/);
            if (sizeMatch) msg.size = parseInt(sizeMatch[1]);

            // Extract MODSEQ (RFC 7162). Format: `MODSEQ (12345)`. Server
            // emits this when the session has ENABLE QRESYNC/CONDSTORE
            // active OR when the client explicitly asks for MODSEQ in the
            // FETCH command. Track on every FETCH so the caller can update
            // its `last_modseq` watermark.
            const modSeqMatch = r.text.match(/MODSEQ\s+\((\d+)\)/);
            if (modSeqMatch) msg.modSeq = parseInt(modSeqMatch[1]);

            // Extract INTERNALDATE
            const dateMatch = r.text.match(/INTERNALDATE\s+"([^"]+)"/);
            if (dateMatch) msg.date = new Date(dateMatch[1]);

            // Extract ENVELOPE
            const envMatch = r.text.match(/ENVELOPE\s+(\(.*\))/);
            if (envMatch) {
                const env = proto.parseEnvelope(envMatch[1]);
                msg.date = msg.date || env.date;
                // Expose the ENVELOPE Date: header (the SENT time) separately
                // from msg.date (INTERNALDATE = server-received time). Callers
                // that want messages to cluster by when they were sent — not by
                // per-account delivery time — use sentDate. Additive/optional.
                msg.sentDate = env.date || undefined;
                msg.subject = env.subject;
                msg.messageId = env.messageId;
                msg.from = env.from;
                msg.to = env.to;
                msg.cc = env.cc;
                msg.bcc = env.bcc;
                msg.sender = env.sender;
                msg.replyTo = env.replyTo;
                msg.inReplyTo = env.inReplyTo;
            }

            // Extract body source and headers from literals tracked by processBuffer
            if (r.literals) {
                const source = r.literals.get("BODY[]");
                if (source) msg.source = source;
                const headers = r.literals.get("BODY[HEADER]");
                if (headers) msg.headers = headers;
            }

            messages.push(msg);
        }
        return messages;
    }
}
